import type { HeatmapResponse } from '@/api/types';
import { collectDescendantIds, type OuNode } from '@/components/common/OuTree/ouTree';

/**
 * Agregación por OU de la respuesta de `GET /v1/analytics/heatmap`.
 *
 * El backend devuelve una fila por participante evaluado (`HeatmapRowDto`), no por OU: la matriz
 * OU × dimensión se arma aquí. Vive en un módulo compartido porque tres pantallas la necesitan
 * con el mismo criterio (AnalyticsView/HeatmapTab, AnalyticsView/OnaTab y ExecutiveView) y
 * cualquier divergencia entre ellas se leería como un bug de datos.
 */

/** Bucket sintético para filas sin `ouId` (participante sin OU asignada). */
export const SIN_OU_KEY = '__sin-ou__';

export interface OuScoreAggregate {
  /** Suma de scores por dimensión, indexada igual que `HeatmapResponse.columns`. */
  sums: number[];
  /** Cantidad de scores numéricos sumados por dimensión (para promediar). */
  counts: number[];
  /** Participantes (filas) agregados en esta OU. */
  participantCount: number;
  /** Encuestas respondidas recibidas por esos participantes. */
  evaluationCount: number;
}

export function createEmptyAggregate(dimensionCount: number): OuScoreAggregate {
  return {
    sums: new Array<number>(dimensionCount).fill(0),
    counts: new Array<number>(dimensionCount).fill(0),
    participantCount: 0,
    evaluationCount: 0,
  };
}

/**
 * Agrupa las filas del heatmap por `ouId`. Las filas sin OU se agrupan bajo {@link SIN_OU_KEY}
 * en vez de descartarse, para no perder observaciones reales en silencio.
 *
 * Un score `null` significa "sin evaluaciones para esa dimensión" y queda fuera del promedio;
 * nunca se trata como un 0, que es un puntaje válido del dominio (ver E-005).
 */
export function aggregateHeatmapByOu(
  heatmap: HeatmapResponse | undefined,
): Map<string, OuScoreAggregate> {
  const aggregates = new Map<string, OuScoreAggregate>();
  if (!heatmap) return aggregates;

  const dimensionCount = heatmap.columns.length;
  for (const row of heatmap.rows) {
    const ouKey = row.ouId && row.ouId.trim().length > 0 ? row.ouId : SIN_OU_KEY;
    let aggregate = aggregates.get(ouKey);
    if (!aggregate) {
      aggregate = createEmptyAggregate(dimensionCount);
      aggregates.set(ouKey, aggregate);
    }
    aggregate.participantCount += 1;
    aggregate.evaluationCount += row.evaluations ?? 0;
    row.scores.forEach((score, index) => {
      if (typeof score === 'number' && index < dimensionCount) {
        aggregate.sums[index] += score;
        aggregate.counts[index] += 1;
      }
    });
  }
  return aggregates;
}

/** Suma `source` dentro de `target`, dimensión por dimensión. */
function accumulate(target: OuScoreAggregate, source: OuScoreAggregate): void {
  target.participantCount += source.participantCount;
  target.evaluationCount += source.evaluationCount;
  source.sums.forEach((sum, index) => { target.sums[index] += sum; });
  source.counts.forEach((count, index) => { target.counts[index] += count; });
}

/**
 * Agregado de un nodo del árbol de OUs: suma la de TODOS sus descendientes reales, no solo la de
 * sus hijos directos — así una OU sigue mostrando datos aunque los participantes cuelguen varios
 * niveles más abajo.
 */
export function mergeAggregateForNode(
  node: OuNode,
  ouAggregates: Map<string, OuScoreAggregate>,
  dimensionCount: number,
): OuScoreAggregate {
  const descendantIds = new Set<string>();
  collectDescendantIds(node, descendantIds);

  const merged = createEmptyAggregate(dimensionCount);
  for (const ouId of descendantIds) {
    const aggregate = ouAggregates.get(ouId);
    if (aggregate) accumulate(merged, aggregate);
  }
  return merged;
}

/**
 * Agregado de las personas asignadas a la OU del nodo y no a alguna de sus hijas.
 *
 * Al bajar un nivel del árbol la tabla lista solo las OUs hijas, así que sin esta fila las
 * personas asignadas directamente al nodo abierto desaparecían de la vista y el nivel dejaba de
 * sumar lo mismo que el nivel superior. En este despliegue es el caso de la mayoría: el sync de
 * Azure AD asigna a las personas a la OU raíz de su región.
 */
export function aggregateDirectlyAssigned(
  node: OuNode,
  ouAggregates: Map<string, OuScoreAggregate>,
  dimensionCount: number,
): OuScoreAggregate {
  const own = ouAggregates.get(node.ou.id);
  const direct = createEmptyAggregate(dimensionCount);
  if (own) accumulate(direct, own);
  return direct;
}

/** Promedio de la dimensión en la posición `index`, o `null` si no hubo evaluaciones. */
export function averageForFundamental(
  aggregate: OuScoreAggregate,
  index: number,
): number | null {
  const count = aggregate.counts[index] ?? 0;
  if (count === 0) return null;
  return (aggregate.sums[index] ?? 0) / count;
}

/** Promedio de todas las dimensiones con dato, o `null` si el agregado no tiene ninguna. */
export function overallAverage(aggregate: OuScoreAggregate): number | null {
  const averages = aggregate.counts
    .map((_unused, index) => averageForFundamental(aggregate, index))
    .filter((average): average is number => average !== null);
  if (averages.length === 0) return null;
  return averages.reduce((sum, average) => sum + average, 0) / averages.length;
}
